import { useCallback } from 'react';

import { useFeatureValue } from '@growthbook/growthbook-react';

import { FeatureToggleEnum } from '@nocowanie/core/enums/feature-toggle.enum';
import { CommonModalsEnum } from '@nocowanie/common-ui/enums/common-modals.enum';
import { SearchEngineModel } from '@nocowanie/common-ui/models/search-engine.model';

import { extractGeoIdAndTypeFromAutocompleteItem } from '@app/helpers/autocomplete.helpers';
import {
    isLocationListingNavigation,
    navigateToLocationListing,
} from '@app/helpers/search.helpers';
import { AutocompleteItemModel } from '@app/models/elastic-search';
import { SearchFilters } from '@app/models/search.model';

/**
 * Shared hook used by both ListingSearchBar and SearchEngineWrapper.
 * Returns a callback that checks if the selected autocomplete item should redirect
 * to a new SSR listing page (based on the GrowthBook `USE_LISTING_SSR` flag).
 *
 * Returns `true` when the redirect has been performed (caller should skip its own
 * default navigation), `false` otherwise.
 */
export function useSsrListingRedirect() {
    const ssrUrlList = useFeatureValue(FeatureToggleEnum.USE_LISTING_SSR, []);

    const tryRedirectToSsrListing = useCallback(
        ({
            selectedItem,
            finalData,
            filters,
            setCookie,
        }: {
            selectedItem: AutocompleteItemModel | null | undefined;
            finalData: SearchEngineModel;
            filters: Partial<SearchFilters>;
            setCookie?: (data: SearchEngineModel) => void;
        }): boolean => {
            const locationData = selectedItem
                ? extractGeoIdAndTypeFromAutocompleteItem(selectedItem)
                : null;

            if (!isLocationListingNavigation(locationData, selectedItem, ssrUrlList)) {
                return false;
            }

            sessionStorage.removeItem(CommonModalsEnum.SearchEngine);
            navigateToLocationListing({
                resolvedItem: selectedItem!,
                finalData,
                filters,
                setCookie,
            });
            return true;
        },
        [ssrUrlList],
    );

    return { tryRedirectToSsrListing };
}
